import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import type { EdpRecipientView } from '../../api/employeeDocsTypes.js';
import { Alert, Button, Spinner } from '../../components/ui.js';
import { LinkAccessShell } from '../../employeeDocs/LinkAccessShell.js';
import { ReleasedPackageView } from './ReleasedPackageView.js';

export function UspsDocumentsPage() {
  const { token = '' } = useParams();
  return (
    <LinkAccessShell prefix="usps-documents" token={token} heading="Background check package">
      {(_preview, signOut) => <RecipientPackage token={token} signOut={signOut} />}
    </LinkAccessShell>
  );
}

function RecipientPackage({ token, signOut }: { token: string; signOut: () => Promise<void> }) {
  const [returned, setReturned] = useState(false);
  const view = useQuery({
    queryKey: ['edp', 'usps-documents', token, 'package'],
    queryFn: () => apiRequest<EdpRecipientView>(`/api/usps-documents/${token}/package`),
    retry: false,
  });

  if (returned) {
    return (
      <Alert variant="success" title="Documents returned to VGS">
        <p>
          VGS has been told which documents to correct and why. Your access to this package has
          ended; you will receive a new link once it is re-released.
        </p>
      </Alert>
    );
  }
  if (view.isLoading) return <Spinner label="Loading the package" />;
  if (view.isError || !view.data) {
    return (
      <Alert variant="error" title="Your session has ended">
        <Button variant="secondary" className="mt-3" onClick={() => void signOut()}>
          Start again
        </Button>
      </Alert>
    );
  }

  return (
    <ReleasedPackageView
      view={view.data}
      fileUrl={(fileId, inline) =>
        `/api/usps-documents/${token}/files/${fileId}${inline ? '?inline=1' : ''}`
      }
      archiveUrl={`/api/usps-documents/${token}/archive`}
      acceptUrl={`/api/usps-documents/${token}/accept`}
      returnUrl={`/api/usps-documents/${token}/return`}
      onChanged={async () => {
        await view.refetch();
      }}
      onReturned={() => setReturned(true)}
      actions={
        <Button variant="ghost" onClick={() => void signOut()}>
          Sign out
        </Button>
      }
    />
  );
}
